import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState, phoneRegex } from '@opu/utils';
import { useHistory, useLocation } from 'react-router-dom';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useTranslation } from 'react-i18next';

const useForm = ({ role }) => {
  let history = useHistory();
  let location = useLocation();
  const [establishments, setEstablishments] = useState([]);
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.params && location.params.id) {
      if (role !== 'ESTABLISHMENT_USER') {
        getEstablishments();
      }
      getStudent();
    } else {
      history.push('/student');
    }
  }, []);

  const back = () => {
    history.goBack();
  };

  const getEstablishments = () => {
    setLoading(true);
    AxiosInstance.get('/establishment')
      .then((res) => {
        setEstablishments(res.data.items);
      })
      .catch((error) => {
        Swal.fire(t('establishment.ERROR_GET_ESTABLISHMENT'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getStudent = () => {
    setLoading(true);

    AxiosInstance.get('/student/' + location.params.id)
      .then((response) => {
        let inputValues = {
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          email: response.data.user.email,
          gender: response.data.user.gender,
          phone: response.data.user.phone,
          bac_matricule: response.data.user.bac_matricule,
          birth_date: response.data.user.birth_date ? response.data.user.birth_date.split('T')[0] : '', // Format for date input
          level: response.data.level,
          establishmentId: role === 'ESTABLISHMENT_USER' ? null : response.data.establishment,
        };
        updateFormState(inputValues);
      })
      .catch((error) => {
        Swal.fire(t('student.ERROR_GET_STUDENT'), '', 'error');
        history.push('/student');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateStudent = () => {
    setLoading(true);

    let establishmentId = null;
    if (role !== 'ESTABLISHMENT_USER') {
      if (formState.inputValues.establishmentId && formState.inputValues.establishmentId.id)
        establishmentId = formState.inputValues.establishmentId.id;
      else establishmentId = formState.inputValues.establishmentId;
    }
    let formattedBirthDate = null;
    if (formState.inputValues.birth_date) {
      const date = new Date(formState.inputValues.birth_date);
      if (!Number.isNaN(date.getTime())) {
        formattedBirthDate = date.toISOString().split('T')[0];
      }
    }

    const rawGender = (formState.inputValues.gender || '').toString().trim();
    const gender =
      rawGender === 'F' || rawGender.toLowerCase().startsWith('f')
        ? 'F'
        : rawGender === 'M' || rawGender.toLowerCase().startsWith('m')
        ? 'M'
        : undefined;
    const phone = (formState.inputValues.phone || '').toString().replace(/\s+/g, '');
    const bac_matricule = (formState.inputValues.bac_matricule || '').toString().replace(/[\s-]+/g, '').trim();

    AxiosInstance.patch('/student/' + location.params.id, {
      firstName: formState.inputValues.firstName || undefined,
      lastName: formState.inputValues.lastName || undefined,
      gender,
      email: formState.inputValues.email || undefined,
      phone: phone || undefined,
      birth_date: formattedBirthDate,
      bac_matricule: bac_matricule || undefined,
      level: formState.inputValues.level || undefined,
      establishmentId: establishmentId,
    })
      .then((res) => {
        Swal.fire(t('student.SUCCESS_UPDATE_STUDENT'), '', 'success');
      })
      .catch((error) => {
        const message = error?.data?.message || error?.data || error?.message;
        if (error.status === 400) {
          handleErrorsServer(message);
          const text = Array.isArray(message) ? message.join(', ') : String(message || '');
          Swal.fire(t('student.ERROR_UPDATE_STUDENT'), text, 'error');
        } else Swal.fire(t('student.ERROR_UPDATE_STUDENT'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inputValues = {
    firstName: { value: '', validate: { required: false } },
    lastName: { value: '', validate: { required: true } },
    email: { value: '', validate: { required: false, pattern: emailRegex } },
    bac_matricule: { value: '', validate: { required: true } },
    birth_date: { value: '', validate: { required: false } },
    phone: { value: '', validate: { required: false } },
    gender: { value: '', validate: { required: false } },
    level: { value: '', validate: { required: false } },
    establishmentId: { value: '', validate: { required: role === 'ESTABLISHMENT_USER' ? false : true } },
  };

  const { formState, submitHandler, updateFormState, field, handleErrorsServer } = FormState(inputValues, updateStudent);

  return {
    submitHandler,
    back,
    loading,
    field,
    establishments,
  };
};

export default useForm;
